import { useMutation, useQueryClient } from '@tanstack/react-query';
import consentTemplatesApi from '@/features/dashboard/consentTemplates/api/consentTemplatesApi.ts';
import { consentTemplatesApiQueryKeys } from '@/features/dashboard/consentTemplates/api/consentTemplatesApi.queryKeys.ts';
import { ConsentTemplateFormData } from '@/features/dashboard/consentTemplates/schemas/consentTemplateSchema.ts';
import { consentTemplateMapper } from '@/features/dashboard/consentTemplates/types/ConsentTemplateDto.mapper.ts';
import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';
import { FileAction } from '@/schemas/fileFieldSchema.ts';
import useUploadConsentFile from '@/features/dashboard/consentTemplates/hooks/useUploadConsentFile.ts';
import { toast } from 'sonner';

export default function useCreateConsentTemplate() {
  const queryClient = useQueryClient();
  const uploadMutation = useUploadConsentFile();

  const mutation = useMutation({
    mutationFn: async (data: ConsentTemplateFormData) => {
      if (data.type === ConsentType.FILE) {
        if (data.consentFile.action === FileAction.CREATE) {
          const result = await uploadMutation.mutateAsync(
            data.consentFile.file!,
          );
          data = {
            ...data,
            consentFile: { ...data.consentFile, fileId: result.fileId },
          };
        } else if (!data.consentFile.fileId) {
          throw new Error('Plik jest wymagany');
        }
      }
      const dto = consentTemplateMapper.fromFormToDto(data);
      return consentTemplatesApi.create(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: consentTemplatesApiQueryKeys.all(),
      });
      toast.success('Szablon zgody został utworzony');
    },
    onError: (error: Error) => {
      toast.error('Wystąpił błąd', {
        description:
          error.message || 'Wystąpił błąd podczas tworzenia szablonu zgody.',
      });
    },
  });

  return {
    ...mutation,
    isPending: mutation.isPending || uploadMutation.isPending,
  };
}
